import utils from 'web3-utils';
import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';

import { ActionStatusEnum } from '../constants';

const BigNumber = require('bignumber.js');
const currencyFormatter = require('currency-formatter');

export const { isBN } = utils.BN;

/**
 * Determine if a value is a valid integer.
 * String is an allowed type to allow for reading from an input component,
 * and/or representing large numbers outside javascript's limitations.
 *
 * @param {string|number|BN} value
 *
 * @returns {bool}
 */
export const isInteger = (value) => {
  const integer = value.toString();
  // Reject if not a basic valid number.
  const regex = /^[0-9]+$/;
  return regex.test(integer);
};

/**
 * Return a properly formatted number in fiat currency.
 *
 * @param {BN} amountInWei
 * @param {string} currencyCode
 * @param {number|BigNumber} currencyExchangeRate
 * @param {string} suffix
 *
 * @returns {string} the amount formatted for the currency.
 */
export const weiToFiat = (amountInWei, currencyCode, currencyExchangeRate, suffix = '') => {
  if (currencyCode && currencyExchangeRate && isBN(amountInWei)) {
    const rate = BigNumber(currencyExchangeRate);
    const amountInFiat = rate.times(utils.fromWei(amountInWei, 'ether'));
    return `${currencyFormatter.format(amountInFiat, {
      code: currencyCode.toUpperCase(),
    })} ${suffix}`.trim();
  }
  return '--';
};

/**
 * Determine if a value is a valid (min/max bounds) ETH amount.
 * String is an allowed type for reading from an input component,
 * and/or representing large numbers outside javascript's limitations.
 *
 * @param {string|number} value
 *
 * @returns {bool}
 */
export const isValidEther = (value) => {
  const ether = value.toString();
  // Reject if not a basic valid number.
  const regex = /^([0-9]*[.])?[0-9]+$/;
  if (ether === '' || !regex.test(ether)) {
    return false;
  }
  const parts = ether.split('.');
  if (parts.length > 2) {
    // Invalid number.
    return false;
  } else if (parts.length === 2) {
    // Must be 18 decimal places or less, and has a decimal place.
    if (parts[1].length > 18 || parts[1].length === 0) {
      return false;
    }
  }
  // TODO: check max value?
  return true;
};

/**
 * Gets the textual description of current payment status based on actual status and action status.
 * Status is the current status in the contract.
 * Action status is a local action that is pending confirmation.
 *
 * @param {number} status
 * @param {number} actionStatus
 *
 * @returns {string}
 */
export const getPaymentStatusText = (status, actionStatus = ActionStatusEnum.pending) => {
  switch (status) {
    case ActionStatusEnum.cancelled:
      return 'Cancelled';
    case ActionStatusEnum.pending: {
      // Is there an action happening?
      if (actionStatus === ActionStatusEnum.cancelled) {
        return 'Cancelling';
      }
      if (actionStatus === ActionStatusEnum.accepted) {
        return 'Accepting';
      }
      return 'Pending';
    }
    case ActionStatusEnum.accepted:
      return 'Accepted';
    case ActionStatusEnum.failed:
      return 'Failed';
    default:
      return 'Processing';
  }
};

/**
 * Calculate the payment fee based on the amount,
 * the payment fee percentage, and payment discount percentage.
 *
 * @param {string} amountInWei
 * @param {BigNumber} paymentFeePercentage
 * @param {BigNumber} paymentDiscount
 *
 * @returns {BN} paymentFee
 */
export const calculatePaymentFee = (
  amountInWei,
  paymentFeePercentage,
  paymentDiscountPercentage,
) => {
  if (
    !isInteger(amountInWei) ||
    !BigNumber.isBigNumber(paymentFeePercentage) ||
    !BigNumber.isBigNumber(paymentDiscountPercentage)
  ) {
    return safeToBn(0);
  }

  // Make sure BigNumber keeps all values output with toString(10) as integers.
  const BN = BigNumber.clone({ DECIMAL_PLACES: 0 });

  const paymentFee = BN(paymentFeePercentage).times(amountInWei);
  const discount = BN(paymentFee).times(paymentDiscountPercentage);
  return safeToBn(paymentFee.minus(discount).toString(10));
};

export const PropTypeBigNumber = (props, propName, componentName) => {
  if (props[propName] && !BigNumber.isBigNumber(props[propName])) {
    return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`. Validation failed.`);
  }
  return null;
};

/**
 * Check that there is enough funds to send the payment amount + fee.
 *
 * Note: paymentFee is optional.
 *
 * @param {BN} amount
 * @param {BN} balance
 * @param {BN?} paymentFee
 *
 * @returns {Boolean}
 */
export const validateBalance = (amount, balance, paymentFee = safeToBn(0)) => {
  if (isBN(amount)) {
    const totalAmount = amount.add(paymentFee);
    return isBN(balance) && balance.gt(totalAmount);
  }
  return false;
};



// WEBPACK FOOTER //
// ./src/utilities/helpers.js